import { Injectable } from "@angular/core";
import { PasskeyService } from "./passkey.service";

interface PromptState {
  dismissCount: number;
  cooldownUntil: number | null;
  permanentlySuppressed: boolean;
}

/**
 * Yahoo-style post-password Passkey prompt.
 * - Account-level suppress: 60-day cooldown, 3 dismissals → permanent
 * - Browser-level enroll flag: prompt again on a new PC even if the account
 *   already has Passkeys (so users can "Create passkey on this device").
 */
@Injectable({
  providedIn: "root",
})
export class PasskeyPromptService {
  static readonly COOLDOWN_DAYS = 60;
  static readonly MAX_DISMISSES = 3;
  private static readonly STORAGE_PREFIX = "fc.passkey.prompt.";
  private static readonly ENROLLED_PREFIX = "fc.passkey.enrolledOnBrowser.";

  constructor(private passkeyService: PasskeyService) {}

  async shouldAutoPrompt(email: string): Promise<boolean> {
    if (!email || !this.passkeyService.isSupported()) {
      return false;
    }

    if (this.hasEnrolledOnThisBrowser(email)) {
      return false;
    }

    const state = this.readState(email);
    if (state.permanentlySuppressed) {
      return false;
    }
    if (state.cooldownUntil && Date.now() < state.cooldownUntil) {
      return false;
    }

    // Still prompt on a new browser/device even if the account already has
    // Passkeys elsewhere (Yahoo: create passkey for this device).
    return true;
  }

  recordDismiss(email: string): void {
    if (!email) {
      return;
    }
    const state = this.readState(email);
    const dismissCount = (state.dismissCount || 0) + 1;
    const permanentlySuppressed =
      dismissCount >= PasskeyPromptService.MAX_DISMISSES;
    const cooldownUntil = permanentlySuppressed
      ? null
      : Date.now() +
        PasskeyPromptService.COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

    this.writeState(email, {
      dismissCount,
      cooldownUntil,
      permanentlySuppressed,
    });
  }

  markEnrolledOnThisBrowser(email: string): void {
    if (!email) {
      return;
    }
    try {
      localStorage.setItem(this.enrolledKey(email), "1");
    } catch {
      // ignore
    }
  }

  clearSuppression(email: string): void {
    if (!email) {
      return;
    }
    try {
      localStorage.removeItem(this.storageKey(email));
    } catch {
      // ignore storage errors
    }
    this.markEnrolledOnThisBrowser(email);
  }

  private hasEnrolledOnThisBrowser(email: string): boolean {
    try {
      return localStorage.getItem(this.enrolledKey(email)) === "1";
    } catch {
      return false;
    }
  }

  private readState(email: string): PromptState {
    try {
      const raw = localStorage.getItem(this.storageKey(email));
      if (!raw) {
        return {
          dismissCount: 0,
          cooldownUntil: null,
          permanentlySuppressed: false,
        };
      }
      const parsed = JSON.parse(raw);
      return {
        dismissCount: Number(parsed.dismissCount) || 0,
        cooldownUntil: parsed.cooldownUntil
          ? Number(parsed.cooldownUntil)
          : null,
        permanentlySuppressed: !!parsed.permanentlySuppressed,
      };
    } catch {
      return {
        dismissCount: 0,
        cooldownUntil: null,
        permanentlySuppressed: false,
      };
    }
  }

  private writeState(email: string, state: PromptState): void {
    try {
      localStorage.setItem(this.storageKey(email), JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }

  private storageKey(email: string): string {
    const host =
      typeof window !== "undefined" ? window.location.hostname : "unknown";
    const normalized = (email || "").trim().toLowerCase();
    return `${PasskeyPromptService.STORAGE_PREFIX}${host}:${normalized}`;
  }

  private enrolledKey(email: string): string {
    const host =
      typeof window !== "undefined" ? window.location.hostname : "unknown";
    const normalized = (email || "").trim().toLowerCase();
    return `${PasskeyPromptService.ENROLLED_PREFIX}${host}:${normalized}`;
  }
}
